import { Injectable } from '@angular/core';
import { CaseFilePartyItemCreation, CaseFilePartyRoleItemCreation, PartyLocatorCreation } from 'com.trumbull.phx.casefile.api-client';
import { RecordRetrievalPartyItemCreation, RecordRetrievalPartyRoleItemCreation } from 'com.trumbull.phx.recordretrievalobjective.api-client';
import * as _ from 'lodash';
import { PartyRoleCode, PartyTypeCode, RequestRef, isUuidRef } from './place-order.model';
import { CreateUuidService } from '../../services/create-uuid/create-uuid.service';
import { RecordFormData } from '../records/records.model';

/**
 * Type extension for RecordRetrievalPartyRoleItemCreation to support requestUuids.
 * The API client may not yet include this field in its type definitions.
 */
interface RecordRetrievalRoleWithUuids extends RecordRetrievalPartyRoleItemCreation {
  requestUuids?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PlaceOrderPartyFactoryService {
  constructor(private createUuidService: CreateUuidService) {}
  private toStringValue(value:any): string {
    return value === undefined || value === null ?  '' : String(value);
  }
  getRetrievalObjectivePartyByTypeCode(code: PartyTypeCode, displayName: any, alias?: any): RecordRetrievalPartyItemCreation {
    let party: RecordRetrievalPartyItemCreation = {};

    switch (code) {
      case PartyTypeCode.Person:
        const retrievalContactName = this.getNames(displayName);
        party.party = {
          typeCode: 'Person',
          displayName,
          names: [
            {
              function: 'first-name',
              text: retrievalContactName[0]
            },
            {
              function: 'last-name',
              text: retrievalContactName[1] && retrievalContactName[1].length > 0 ? retrievalContactName[1] : ' '
            }
          ],
          locators: []
        };

        if (alias) {
          party.party?.names?.push({
            function: 'alias',
            text: alias
          });
        }
        break;
      case PartyTypeCode.Organization:
        party.party = {
          typeCode: 'Organization',
          displayName,
          names: [
            {
              function: 'business-name',
              text: displayName
            }
          ],
          locators: []
        };
        break;
    }

    return party;
  }

  getCasePartyByTypeCode(code: PartyTypeCode, displayName: any, data?: any): CaseFilePartyItemCreation {
    let party: CaseFilePartyItemCreation = {};

    switch (code) {
      case PartyTypeCode.Person:
        const caseContactName = this.getNames(displayName);
        party.party = {
          typeCode: 'Person',
          displayName,
          names: [
            {
              function: 'first-name',
              text: caseContactName[0]
            },
            {
              function: 'last-name',
              text: caseContactName[1] && caseContactName[1].length > 0 ? caseContactName[1] : ' '
            }
          ],
          locators: []
        };

        if (data) {
          party.party?.names?.push({
            function: 'title',
            text: data
          });
        }
        break;
      case PartyTypeCode.Organization:
        party.party = {
          typeCode: 'Organization',
          displayName,
          names: [
            {
              function: 'business-name',
              text: displayName
            }
          ],
          locators: []
        };

        if (data) {
          party.party?.names?.push({
            function: 'contact-name',
            text: data
          });
        }
        break;
    }

    return party;
  }

  getRole(code: PartyRoleCode, data?: any): CaseFilePartyRoleItemCreation {
    const roleItem: CaseFilePartyRoleItemCreation = {
      delete: false
    };

    switch (code) {
      case PartyRoleCode.Paralegal:
        roleItem.roleCode = 'defendant-paralegal';
        break;
      case PartyRoleCode.Other:
        roleItem.roleCode = 'contact-other';
        break;
      case PartyRoleCode.Attorney:
        roleItem.roleCode = 'defendant-attorney';
        roleItem.attorney = {
          barNumber: data
        };
        break;
      case PartyRoleCode.PlaintiffAttorney:
      case PartyRoleCode.CoDefendantAttorney:
        roleItem.roleCode = code;
        roleItem.attorney = {
          barNumber: data
        };
        break;
      case PartyRoleCode.Defendant:
      case PartyRoleCode.Plaintiff:
      case PartyRoleCode.BillTo:
      case PartyRoleCode.Adjuster:
      case PartyRoleCode.DeliverTo:
      case PartyRoleCode.RecordSubject:
      case PartyRoleCode.RecordsCustodian:
      case PartyRoleCode.MedicalProvider:
      case PartyRoleCode.AmbulanceService:
        roleItem.roleCode = code;
        break;
      case PartyRoleCode.Court:
        roleItem.roleCode = code;
        roleItem.court = {
          judgeName: data?.item1,
          jurisdiction: data?.item2
        };
        break;
    }

    return roleItem;
  }

  getEmailLocator(email?: string): PartyLocatorCreation {
    return {
      typeCode: 'email',
      email: {
        purpose: 'work',
        address: this.toStringValue(email)
      }
    };
  }

  getFaxLocator(fax?: string): PartyLocatorCreation {
    return {
      typeCode: 'phone',
      phone: {
        purpose: 'fax',
        number: this.toStringValue(fax)
      }
    };
  }


  getPhoneLocator(phone?: string | number, extn?: string | number): PartyLocatorCreation {
    const locatorItem: PartyLocatorCreation = {
      typeCode: 'phone',
      phone: {
        purpose: 'work',
        number: this.toStringValue(phone)
      }
    };

    if (extn) {
      locatorItem.phone!.extension = this.toStringValue(extn);
    }

    return locatorItem;
  }
getLocationLocator(
  lineOne: string | number,
  city: string,
  stateCode: string,
  postalCode: string | number,
  lineTwo: string = ''
): PartyLocatorCreation {
  return {
    typeCode: 'location',
    location: {
      location: {
        typeCode: 'us',
        usAddress: {
          lineOne: this.toStringValue(lineOne),
          lineTwo: this.toStringValue(lineTwo),
          city: this.toStringValue(city),
          stateCode: this.toStringValue(stateCode),
          postalCode: this.toStringValue(postalCode)
        }
      }
    }
  };
}

  checkAddressValid(
    streetAddress?: string,
    city?: string,
    state?: string,
    zip?: string,
    streetAddress2?: string
  ): boolean {
    return !!(streetAddress || city || state || zip || streetAddress2);
  }

  getNames(input: any): string[] {
    const result = _.split(input, ' ');
    const firstPart = result[0];
    const secondPart = result.length > 1 ? result.slice(1).join(' ') : '';

    return [firstPart, secondPart];
  }
  /**
   * Builds a records custodian party entry with proper request reference linking.
   * Used for both create flow (UUID) and edit flow (number) scenarios.
   * 
   * @param item - The record form data containing provider information
   * @param ref - RequestRef discriminated union (kind: 'uuid' | 'number')
   * @returns RecordRetrievalPartyItemCreation with custodian role and linked request
   */
  buildRecordsCustodianParty(item: RecordFormData, ref: RequestRef): RecordRetrievalPartyItemCreation {
    const party = this.getRetrievalObjectivePartyByTypeCode(
      PartyTypeCode.Organization,
      item.providerName
    );

    // Add contact locators
    if (item.phone) {
      party.party?.locators?.push(this.getPhoneLocator(item.phone, item.extn));
    }
    if (item.fax) {
      party.party?.locators?.push(this.getFaxLocator(item.fax));
    }
    if (item.email) {
      party.party?.locators?.push(this.getEmailLocator(item.email));
    }
    if (this.checkAddressValid(item.streetAddress, item.cityName, item.stateName?.value, item.zipCode, item.aptSuiteOther)) {
      party.party?.locators?.push(
        this.getLocationLocator(
          item.streetAddress ?? '',
          item.cityName ?? '',
          item.stateName?.value ?? '',
          item.zipCode ?? '',
          item.aptSuiteOther ?? ''
        )
      );
    }

    // Set role and link to request based on ref.kind
    party.roles = [this.getRole(PartyRoleCode.RecordsCustodian)];
    const role = party.roles[0] as RecordRetrievalRoleWithUuids;
    
    // Set request reference based on discriminated union kind
    if (isUuidRef(ref)) {
      role.requestUuids = [ref.value];
    } else {
      role.requestNumbers = [ref.value];
    }

    // Set party external ID with null guard
    if (party.party) {
      party.party.keys = this.createUuidService.generatePartyExternalId(item.providerKeys ?? []);
    } else {
      console.error('Party object not initialized in buildRecordsCustodianParty');
    }
    
    return party;
  }

  /**
   * Builds a medical provider party entry with proper request reference linking.
   * Used when a doctor name is specified for a request.
   * 
   * @param item - The record form data containing doctor information
   * @param ref - RequestRef discriminated union (kind: 'uuid' | 'number')
   * @returns RecordRetrievalPartyItemCreation with medical provider role and linked request
   */
  buildMedicalProviderParty(item: RecordFormData, ref: RequestRef): RecordRetrievalPartyItemCreation {
    const party = this.getRetrievalObjectivePartyByTypeCode(
      PartyTypeCode.Person,
      item.doctor!
    );

    // Add title prefix for medical provider
    party.party?.names?.push({ function: 'title', text: 'Dr.' });

    // Set role and link to request based on ref.kind
    party.roles = [this.getRole(PartyRoleCode.MedicalProvider)];
    const role = party.roles[0] as RecordRetrievalRoleWithUuids;
    
    // Set request reference based on discriminated union kind
    if (isUuidRef(ref)) {
      role.requestUuids = [ref.value];
    } else {
      role.requestNumbers = [ref.value];
    }

    // Set party external ID with null guard
    if (party.party) {
      party.party.keys = this.createUuidService.generatePartyExternalId(item.doctorKeys ?? []);
    } else {
      console.error('Party object not initialized in buildMedicalProviderParty');
    }
    
    return party;
  }

  /**
   * Appends a request reference to a record-subject role.
   * Accumulates all request refs into ONE combined record-subject role entry.
   * Handles both UUID (new requests) and number (existing requests) formats.
   * 
   * @param role - The record-subject role to update
   * @param ref - RequestRef discriminated union (kind: 'uuid' | 'number')
   */
  appendRequestRefToSubjectRole(role: RecordRetrievalRoleWithUuids, ref: RequestRef): void {
    if (isUuidRef(ref)) {
      // Initialize array if doesn't exist, then push new UUID
      role.requestUuids = role.requestUuids ?? [];
      role.requestUuids.push(ref.value);
    } else {
      // Initialize array if doesn't exist, then push new number
      role.requestNumbers = role.requestNumbers ?? [];
      role.requestNumbers.push(ref.value);
    }
  }

  /**
   * LEGACY METHOD - Creates a medical provider party using integer-based index.
   * Preserved for backward compatibility.
   * 
   * @deprecated Use buildMedicalProviderParty with RequestRef instead
   * @param item - The record form data containing doctor information
   * @param index - Zero-based index of the record (will be converted to 1-based)
   * @returns RecordRetrievalPartyItemCreation with medical provider role
   */
  createNewMedicalProviderParty(item: any, index: number): RecordRetrievalPartyItemCreation {
    const party = this.getRetrievalObjectivePartyByTypeCode(
      PartyTypeCode.Person,
      item?.doctor
    );
    
    party.party?.names?.push({
      function: 'title',
      text: 'Dr.  '
    });
    
    party.roles = [];
    party.roles?.push(this.getRole(PartyRoleCode.MedicalProvider));
    
    const role = _.find(party.roles, ['roleCode', PartyRoleCode.MedicalProvider]);
    if (role) {
      role.requestUuids = [item.requestUuid];
    }
    
    if (party.party) {
      party.party.keys = party.party.keys || [];
      party.party.keys = this.createUuidService.generatePartyExternalId(item.doctorKeys);
    }
    
    return party;
  }

  /**
   * LEGACY METHOD - Creates a records custodian party using integer-based index.
   * Preserved for backward compatibility.
   * 
   * @deprecated Use buildRecordsCustodianParty with RequestRef instead
   * @param item - The record form data containing provider information
   * @param index - Zero-based index of the record (will be converted to 1-based)
   * @returns RecordRetrievalPartyItemCreation with records custodian role
   */
  createNewRecordCustodianParty(item: any, index: number): RecordRetrievalPartyItemCreation {
    const party = this.getRetrievalObjectivePartyByTypeCode(
      PartyTypeCode.Organization,
      item.providerName
    );
    
    if (item.email) {
      party.party?.locators?.push(this.getEmailLocator(item.email));
    }
    if (item.phone) {
      party.party?.locators?.push(this.getPhoneLocator(item.phone, item.extn));
    }
    if (item.fax) {
      party.party?.locators?.push(this.getFaxLocator(item.fax));
    }
    if (this.checkAddressValid(
      item.streetAddress,
      item.cityName,
      item.stateName?.value,
      item.zipCode,
      item.aptSuiteOther
    )) {
      party.party?.locators?.push(
        this.getLocationLocator(
          item.streetAddress,
          item.cityName,
          item.stateName?.value,
          item.zipCode,
          item.aptSuiteOther
        )
      );
    }
    
    party.roles = [];
    party.roles?.push(this.getRole(PartyRoleCode.RecordsCustodian));
    
    const role = _.find(party.roles, ['roleCode', PartyRoleCode.RecordsCustodian]);
    if (role) {
          role.requestUuids = [item.requestUuid];
    }
    
    if (party.party) {
      party.party.keys = party.party.keys || [];
      party.party.keys = this.createUuidService.generatePartyExternalId(item.providerKeys);
    }
    
    return party;
  }
}